import { defineComponent, h, ref, onBeforeUnmount } from 'vue';
import { ResourceTable, ResourceTableProps, resourceTableProps } from '@/ui/components/table/ResourceTable';
import { Modal } from '@/ui/components/Modal';
import { Button } from '@/ui/components/Button';
import { t } from '@/ui/i18n/i18n';
import { pageSubtitle } from '@/ui/components';
import { Links } from '@/services/api';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { SortDirection } from '@/ui/types';

type GeneralResource = (unknown & {id: string});

interface BulkDeletableTableProps<T = unknown> extends ResourceTableProps<T> {
  recordName: { plural: string; singular: string };
  paginationLinks?: Links;
  loading: boolean;
}

export const BulkDeletableTable = defineComponent<BulkDeletableTableProps>({
  name: 'BulkDeletableTable',
  props: [
    ...resourceTableProps,
    'recordName', 'paginationLinks', 'loading',
  ],
  setup(props, context) {
    const isConfirmationModalOpen = ref(false);

    const needsRefreshContent = () => {
      // if we are not paginating the results we
      // don't need to get a fresh copy of records
      if (!props.paginationLinks) {
        return false;
      }

      return props.paginationLinks.next !== null;
    };

    const onCancel = () => {
      isConfirmationModalOpen.value = false;
    };

    const onConfirmDelete = () => {
      EventBus.$emit(Events.bulkDeleteClickConfirmation);
    };

    const onRecordsDeleted = () => {
      isConfirmationModalOpen.value = false;

      if (needsRefreshContent()) {
        context.emit('needs-refresh-content');
      }
    };

    const onDeleteClick = () => {
      isConfirmationModalOpen.value = true;
    };

    EventBus.$on(Events.bulkDeleteClick, onDeleteClick);
    EventBus.$on(Events.recordsDeleted, onRecordsDeleted);

    onBeforeUnmount(() => {
      EventBus.$off(Events.bulkDeleteClick, onDeleteClick);
      EventBus.$off(Events.recordsDeleted, onRecordsDeleted);
    });

    return function render() {
      return h('div', [
        h(Modal, {
          props: { active: isConfirmationModalOpen.value, width: 'max-content', hideClose: true },
          scopedSlots: {
            actions: () => h('div', [
              h(Button, {
                props: { type: 'is-text', btnClass: 'mr-2 has-text-weight-semibold', disabled: props.loading },
                on: { click: onCancel },
              }, t('modal.cancel')),
              h(Button, {
                props: {
                  loading: props.loading,
                },
                on: { click: onConfirmDelete },
              }, t('bulk-delete.yes-delete')),
            ]),
          },
        }, [
          pageSubtitle(t('bulk-delete.title', { resourceName: props.recordName.plural })),
          h('div', { class: 'mt-4' }, [
            t('bulk-delete.confirmation', {
              resourceName: props.selectedRecords.length > 1 ? props.recordName.plural : props.recordName.singular,
              numberResources: props.selectedRecords.length.toString(),
            }),
          ]),
        ]),
        h(ResourceTable, {
          props: {
            ...props,
            checkable: true,
          },
          scopedSlots: context.slots,
          on: {
            'update:checkedRows': (resources: GeneralResource[]) => {
              context.emit('update:checkedRows', resources);
            },
            click: (entity: unknown) => { context.emit('click', entity); },
            deleted: ({ entity }: { entity: unknown }) => {
              EventBus.$emit(Events.singleRecordDeleted, { entity });
              context.emit('deleted');
            },
            sort: (field: string, direction: SortDirection) => context.emit('sort', field, direction),
          },
        }, []),
      ]);
    };
  },
});
